<template>
  <div class="en-tab-view">
    <div class="tab-bar">
      <div class="tabs-header">
        <en-tab-header v-for="(tab, index) in tabs" :key="index" :bold="bold"
                       :class="{ selected: index === selectedIndex }"
                       class="en-tab-header" @click="selectTab(index)">
          {{ tab.title }}
        </en-tab-header>
      </div>
      <div class="en-tab-placeholder" :class="{bold: bold}"></div>
    </div>

    <div class="en-tab-content">
      <!-- @slot The tab view's content. -->
      <slot></slot>
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted } from 'vue';
import EnTabHeader from 'Components/tabs/EnTabHeader/EnTabHeader';

/**
 * A simple tab view.
 */
const props = defineProps({
  /**
   * Specifies whether the bold style is applied (true) or not (false).
   * - larger font
   * - selected tab has dark background color and inverted text color
   * - hovered tab is grey
   * - thick line below headers is always there
   */
  bold: Boolean,

  /**
   * An array of tab objects.
   */
  refs: {
    type: Array,
    default: () => [],
  }
});

/**
 * Triggers when the selected tab has changed.
 * @event selected-tab-changed
 * @param {Number} i - The index of the selected tab.
 */
const emit = defineEmits(['selected-tab-changed']);

/**
 * The index of the selected tab.
 * @type {Number}
 */
const selectedIndex = ref(0);

/**
 * All the tabs.
 * @type {Array}
 */
const tabs = ref([]);

/**
 * Selects the tab at the given index.
 * @param {Number} i - The index of the tab to select.
 */
const selectTab = (i) => {
  if (tabs.value.length === 0) return;

  if (i >= tabs.value.length) i = tabs.value.length - 1;
  selectedIndex.value = i;

  // loop over all the tabs
  tabs.value.forEach((tab, index) => {
    tab.isActive = index === i;
  });

  emit('selected-tab-changed', i);
};

onMounted(async () => {
  await nextTick();
  tabs.value = props.refs;
  selectTab(0);
});
</script>

<style lang="scss" scoped>
@import "Styles/helper/variables.scss";

.en-tab-view {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.tab-bar {
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: $spacing-s;
}

.tabs-header {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  overflow-x: auto;
  
  @include respond-max-width(md) {
    flex-wrap: wrap;
    overflow-x: visible;
  }
  
  @include respond-max-width(sm) {
    flex-wrap: wrap;
    gap: 2px;
  }
}

.en-tab-header {
  flex: 0 0 auto;
  min-width: fit-content;
  height: $spacing-l;
  white-space: nowrap;
  
  @include respond-max-width(md) {
    font-size: 0.875rem;
    min-width: auto;
  }
  
  @include respond-max-width(sm) {
    font-size: 0.8rem;
    padding: 4px 8px;
    min-width: auto;
    height: 32px;
    white-space: normal;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.en-tab-placeholder {
  width: 100%;
  height: 1px;
  border-bottom: $en-blue thin solid;
  flex-shrink: 0;
  margin-top: 0;

  &.bold {
    border-bottom: $en-blue 4px solid;
  }
}

.en-tab-content {
  padding: $spacing-s;
  width: 100%;
  flex: 1;
}
</style>